'use client'

import { motion } from 'framer-motion'
import { BackgroundBeams } from '../../../_components/_ui/background-beams'
import Link from 'next/link'

export default function DatabasePage() {
    return (
        <div className="min-h-screen w-full relative">
            <BackgroundBeams />
            <div className="relative z-10 container mx-auto px-4 py-16">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="max-w-5xl mx-auto"
                >
                    <div className="mb-8">
                        <Link href="/support/documentation" className="text-indigo-400 hover:text-indigo-300 text-sm">
                            ← Back to Documentation
                        </Link>
                    </div>

                    <h1 className="text-4xl font-bold text-white mb-6">
                        Database Schema & Architecture
                    </h1>
                    
                    <div className="prose prose-lg prose-invert max-w-none">
                        <p className="text-gray-300 text-lg mb-8">
                            Autofocus uses PostgreSQL through Supabase with a carefully designed schema that supports 
                            flexible content management, robust user permissions, and scalable data relationships.
                        </p>

                        {/* Schema Overview */}
                        <div className="bg-neutral-900/50 backdrop-blur-sm rounded-lg p-6 mb-8 border border-neutral-800">
                            <h2 className="text-2xl font-bold text-white mb-4">
                                🗄️ Core Schema Structure
                            </h2>
                            <div className="space-y-6">
                                <div>
                                    <h3 className="text-lg font-semibold text-blue-400 mb-3">Entity Relationship Overview</h3>
                                    <div className="bg-neutral-800 p-4 rounded font-mono text-sm text-gray-300">
                                        <div className="text-center mb-4 text-white font-bold">Data Relationships</div>
                                        <div className="space-y-1">
                                            <div>auth.users → profiles (1:1)</div>
                                            <div>profiles → pages (1:many)</div>
                                            <div>pages → content_blocks (1:many)</div>
                                            <div>users → user_roles (1:many)</div>
                                            <div>roles → permissions (many:many)</div>
                                        </div>
                                    </div>
                                </div>
                                
                                <div className="grid md:grid-cols-2 gap-6">
                                    <div>
                                        <h4 className="font-semibold text-green-400 mb-2">Core Tables</h4>
                                        <ul className="text-gray-300 text-sm space-y-1">
                                            <li>• <code className="bg-neutral-800 px-2 py-1 rounded">auth.users</code> - Supabase authentication</li>
                                            <li>• <code className="bg-neutral-800 px-2 py-1 rounded">profiles</code> - User profile data</li>
                                            <li>• <code className="bg-neutral-800 px-2 py-1 rounded">pages</code> - User-created pages</li>
                                            <li>• <code className="bg-neutral-800 px-2 py-1 rounded">content_blocks</code> - Page content blocks</li>
                                        </ul>
                                    </div>
                                    <div>
                                        <h4 className="font-semibold text-purple-400 mb-2">Permission Tables</h4>
                                        <ul className="text-gray-300 text-sm space-y-1">
                                            <li>• <code className="bg-neutral-800 px-2 py-1 rounded">roles</code> - Available system roles</li>
                                            <li>• <code className="bg-neutral-800 px-2 py-1 rounded">user_roles</code> - User-role assignments</li>
                                            <li>• <code className="bg-neutral-800 px-2 py-1 rounded">permissions</code> - System permissions</li>
                                            <li>• <code className="bg-neutral-800 px-2 py-1 rounded">resource_limits</code> - Subscription limits</li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Core Tables Detail */}
                        <div className="bg-neutral-900/50 backdrop-blur-sm rounded-lg p-6 mb-8 border border-neutral-800">
                            <h2 className="text-2xl font-bold text-white mb-4">
                                📋 Core Tables Schema
                            </h2>
                            
                            <div className="space-y-6">
                                <div>
                                    <h3 className="text-lg font-semibold text-indigo-400 mb-3">profiles</h3>
                                    <p className="text-gray-300 text-sm mb-3">
                                        Extended user information beyond Supabase auth, including display preferences and social links.
                                    </p>
                                    <pre className="bg-neutral-800 p-4 rounded text-xs text-gray-300 overflow-x-auto">
{`CREATE TABLE profiles (
  id UUID PRIMARY KEY REFERENCES auth.users(id) ON DELETE CASCADE,
  username TEXT UNIQUE NOT NULL,
  display_name TEXT,
  bio TEXT,
  avatar_url TEXT,
  social_links JSONB DEFAULT '{}',
  website_url TEXT,
  location TEXT,
  metadata JSONB DEFAULT '{}',
  created_at TIMESTAMPTZ DEFAULT now(),
  updated_at TIMESTAMPTZ DEFAULT now()
);

-- Indexes for performance
CREATE INDEX idx_profiles_username ON profiles(username);
CREATE INDEX idx_profiles_created_at ON profiles(created_at);`}
                                    </pre>
                                </div>

                                <div>
                                    <h3 className="text-lg font-semibold text-green-400 mb-3">pages</h3>
                                    <p className="text-gray-300 text-sm mb-3">
                                        User-created pages with customizable layouts, styling, and metadata configurations.
                                    </p>
                                    <pre className="bg-neutral-800 p-4 rounded text-xs text-gray-300 overflow-x-auto">
{`CREATE TABLE pages (
  id UUID PRIMARY KEY DEFAULT gen_random_uuid(),
  profile_id UUID NOT NULL REFERENCES profiles(id) ON DELETE CASCADE,
  title TEXT NOT NULL,
  slug TEXT NOT NULL,
  description TEXT,
  style TEXT DEFAULT 'default',
  layout TEXT DEFAULT 'stack',
  spacing TEXT DEFAULT 'normal',
  padding TEXT DEFAULT 'normal',
  max_width TEXT DEFAULT 'default',
  metadata JSONB DEFAULT '{}',
  is_public BOOLEAN DEFAULT true,
  created_at TIMESTAMPTZ DEFAULT now(),
  updated_at TIMESTAMPTZ DEFAULT now(),
  UNIQUE(profile_id, slug)
);

-- Indexes
CREATE INDEX idx_pages_profile_id ON pages(profile_id);
CREATE INDEX idx_pages_slug ON pages(slug);
CREATE INDEX idx_pages_public ON pages(is_public) WHERE is_public = true;`}
                                    </pre>
                                </div>

                                <div>
                                    <h3 className="text-lg font-semibold text-orange-400 mb-3">content_blocks</h3>
                                    <p className="text-gray-300 text-sm mb-3">
                                        Flexible content storage with JSONB for type-specific data and ordering support.
                                    </p>
                                    <pre className="bg-neutral-800 p-4 rounded text-xs text-gray-300 overflow-x-auto">
{`CREATE TABLE content_blocks (
  id UUID PRIMARY KEY DEFAULT gen_random_uuid(),
  page_id UUID NOT NULL REFERENCES pages(id) ON DELETE CASCADE,
  type TEXT NOT NULL,
  content JSONB NOT NULL DEFAULT '{}',
  order_index INTEGER NOT NULL DEFAULT 0,
  metadata JSONB DEFAULT '{}',
  created_at TIMESTAMPTZ DEFAULT now(),
  updated_at TIMESTAMPTZ DEFAULT now()
);

-- Indexes for performance
CREATE INDEX idx_content_blocks_page_id ON content_blocks(page_id);
CREATE INDEX idx_content_blocks_order ON content_blocks(page_id, order_index);
CREATE INDEX idx_content_blocks_type ON content_blocks(type);

-- GIN index for JSONB content searching
CREATE INDEX idx_content_blocks_content ON content_blocks USING GIN(content);`}
                                    </pre>
                                </div>
                            </div>
                        </div>

                        {/* Permissions Schema */}
                        <div className="bg-neutral-900/50 backdrop-blur-sm rounded-lg p-6 mb-8 border border-neutral-800">
                            <h2 className="text-2xl font-bold text-white mb-4">
                                🔐 Permissions & Roles Schema
                            </h2>
                            
                            <div className="space-y-6">
                                <div>
                                    <h3 className="text-lg font-semibold text-red-400 mb-3">Role-Based Access Control</h3>
                                    <p className="text-gray-300 text-sm mb-4">
                                        Comprehensive permission system supporting subscription tiers, special roles, and granular permissions.
                                    </p>
                                    
                                    <div className="grid md:grid-cols-2 gap-4">
                                        <div>
                                            <h4 className="font-semibold text-yellow-400 mb-2">roles</h4>
                                            <pre className="bg-neutral-800 p-3 rounded text-xs text-gray-300 overflow-x-auto">
{`CREATE TABLE roles (
  id UUID PRIMARY KEY DEFAULT gen_random_uuid(),
  name TEXT UNIQUE NOT NULL,
  description TEXT,
  role_type TEXT NOT NULL, -- 'permission', 'subscription', 'special'
  created_at TIMESTAMPTZ DEFAULT now(),
  updated_at TIMESTAMPTZ DEFAULT now()
);

-- Default roles
INSERT INTO roles (name, description, role_type) VALUES
('admin', 'Complete application control', 'permission'),
('moderator', 'Content moderation', 'permission'),
('basic', 'Free tier', 'subscription'),
('turbo', 'Turbo subscription', 'subscription'),
('summit', 'Summit subscription', 'subscription'),
('alpha', 'Alpha program', 'special'),
('beta', 'Beta program', 'special');`}
                                            </pre>
                                        </div>
                                        
                                        <div>
                                            <h4 className="font-semibold text-cyan-400 mb-2">user_roles</h4>
                                            <pre className="bg-neutral-800 p-3 rounded text-xs text-gray-300 overflow-x-auto">
{`CREATE TABLE user_roles (
  id UUID PRIMARY KEY DEFAULT gen_random_uuid(),
  user_id UUID NOT NULL REFERENCES auth.users(id) ON DELETE CASCADE,
  role_id UUID NOT NULL REFERENCES roles(id) ON DELETE CASCADE,
  created_at TIMESTAMPTZ DEFAULT now(),
  updated_at TIMESTAMPTZ DEFAULT now(),
  expires_at TIMESTAMPTZ, -- For subscription roles
  UNIQUE (user_id, role_id)
);

-- Indexes
CREATE INDEX idx_user_roles_user_id ON user_roles(user_id);
CREATE INDEX idx_user_roles_role_id ON user_roles(role_id);
CREATE INDEX idx_user_roles_expires ON user_roles(expires_at) WHERE expires_at IS NOT NULL;`}
                                            </pre>
                                        </div>
                                    </div>
                                </div>
                                
                                <div>
                                    <h3 className="text-lg font-semibold text-purple-400 mb-3">Resource Limits</h3>
                                    <p className="text-gray-300 text-sm mb-3">
                                        Subscription-based limits for pages, blocks, and content size by role.
                                    </p>
                                    <pre className="bg-neutral-800 p-4 rounded text-xs text-gray-300 overflow-x-auto">
{`CREATE TABLE resource_limits (
  id UUID PRIMARY KEY DEFAULT gen_random_uuid(),
  role_id UUID NOT NULL REFERENCES roles(id) ON DELETE CASCADE,
  resource_type TEXT NOT NULL, -- 'page', 'block', 'storage'
  resource_subtype TEXT, -- 'image_block', 'video_block', etc.
  max_count INTEGER DEFAULT -1, -- -1 = unlimited
  max_size INTEGER DEFAULT -1, -- in KB, -1 = unlimited
  created_at TIMESTAMPTZ DEFAULT now()
);

-- Example limits
INSERT INTO resource_limits (role_id, resource_type, resource_subtype, max_count, max_size) 
SELECT id, 'page', NULL, 3, -1 FROM roles WHERE name = 'basic';

INSERT INTO resource_limits (role_id, resource_type, resource_subtype, max_count, max_size)
SELECT id, 'block', 'image_block', 5, 1024 FROM roles WHERE name = 'basic';`}
                                    </pre>
                                </div>
                            </div>
                        </div>

                        {/* Row Level Security */}
                        <div className="bg-neutral-900/50 backdrop-blur-sm rounded-lg p-6 mb-8 border border-neutral-800">
                            <h2 className="text-2xl font-bold text-white mb-4">
                                🛡️ Row Level Security (RLS)
                            </h2>
                            <div className="space-y-6">
                                <div>
                                    <h3 className="text-lg font-semibold text-green-400 mb-3">Security Policies</h3>
                                    <p className="text-gray-300 text-sm mb-4">
                                        PostgreSQL RLS policies ensure users can only access their own data and appropriate public content.
                                    </p>
                                    
                                    <div className="space-y-4">
                                        <div>
                                            <h4 className="font-semibold text-blue-400 mb-2">Profile Access Policy</h4>
                                            <pre className="bg-neutral-800 p-3 rounded text-xs text-gray-300 overflow-x-auto">
{`-- Users can read all public profiles
CREATE POLICY "Public profiles are viewable by everyone"
  ON profiles FOR SELECT
  USING (true);

-- Users can only update their own profile
CREATE POLICY "Users can update own profile"
  ON profiles FOR UPDATE
  USING (auth.uid() = id);`}
                                            </pre>
                                        </div>
                                        
                                        <div>
                                            <h4 className="font-semibold text-purple-400 mb-2">Page Access Policy</h4>
                                            <pre className="bg-neutral-800 p-3 rounded text-xs text-gray-300 overflow-x-auto">
{`-- Public pages are viewable by everyone
CREATE POLICY "Public pages are viewable by everyone"
  ON pages FOR SELECT
  USING (is_public = true);

-- Users can access their own pages (public or private)
CREATE POLICY "Users can access own pages"
  ON pages FOR ALL
  USING (profile_id IN (
    SELECT id FROM profiles WHERE id = auth.uid()
  ));`}
                                            </pre>
                                        </div>
                                        
                                        <div>
                                            <h4 className="font-semibold text-orange-400 mb-2">Content Block Policy</h4>
                                            <pre className="bg-neutral-800 p-3 rounded text-xs text-gray-300 overflow-x-auto">
{`-- Blocks inherit visibility from their page
CREATE POLICY "Blocks follow page visibility"
  ON content_blocks FOR SELECT
  USING (
    page_id IN (
      SELECT id FROM pages 
      WHERE is_public = true
      OR profile_id = auth.uid()
    )
  );

-- Only page owners can modify blocks
CREATE POLICY "Users can modify own page blocks"
  ON content_blocks FOR ALL
  USING (
    page_id IN (
      SELECT id FROM pages WHERE profile_id = auth.uid()
    )
  );`}
                                            </pre>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Database Functions */}
                        <div className="bg-neutral-900/50 backdrop-blur-sm rounded-lg p-6 mb-8 border border-neutral-800">
                            <h2 className="text-2xl font-bold text-white mb-4">
                                ⚙️ Database Functions & Triggers
                            </h2>
                            <div className="space-y-6">
                                <div>
                                    <h3 className="text-lg font-semibold text-cyan-400 mb-3">Custom Functions</h3>
                                    <p className="text-gray-300 text-sm mb-4">
                                        PostgreSQL functions for complex queries and business logic.
                                    </p>
                                    
                                    <div className="space-y-4">
                                        <div>
                                            <h4 className="font-semibold text-yellow-400 mb-2">get_user_roles(user_id)</h4>
                                            <pre className="bg-neutral-800 p-3 rounded text-xs text-gray-300 overflow-x-auto">
{`CREATE OR REPLACE FUNCTION get_user_roles(user_id UUID)
RETURNS TABLE(role_name TEXT, role_type TEXT, expires_at TIMESTAMPTZ)
LANGUAGE sql SECURITY DEFINER
AS $$
  SELECT r.name, r.role_type, ur.expires_at
  FROM user_roles ur
  JOIN roles r ON ur.role_id = r.id
  WHERE ur.user_id = $1
  AND (ur.expires_at IS NULL OR ur.expires_at > now());
$$;`}
                                            </pre>
                                        </div>
                                        
                                        <div>
                                            <h4 className="font-semibold text-green-400 mb-2">get_subscription_level(user_id)</h4>
                                            <pre className="bg-neutral-800 p-3 rounded text-xs text-gray-300 overflow-x-auto">
{`CREATE OR REPLACE FUNCTION get_subscription_level(user_id UUID)
RETURNS TEXT
LANGUAGE sql SECURITY DEFINER
AS $$
  SELECT COALESCE(
    (SELECT r.name FROM user_roles ur
     JOIN roles r ON ur.role_id = r.id
     WHERE ur.user_id = $1 
     AND r.role_type = 'subscription'
     AND (ur.expires_at IS NULL OR ur.expires_at > now())
     ORDER BY 
       CASE r.name
         WHEN 'lifetime_summit' THEN 5
         WHEN 'lifetime_turbo' THEN 4
         WHEN 'summit' THEN 3
         WHEN 'turbo' THEN 2
         WHEN 'basic' THEN 1
         ELSE 0
       END DESC
     LIMIT 1),
    'basic'
  );
$$;`}
                                            </pre>
                                        </div>
                                    </div>
                                </div>
                                
                                <div>
                                    <h3 className="text-lg font-semibold text-red-400 mb-3">Triggers</h3>
                                    <div className="space-y-4">
                                        <div>
                                            <h4 className="font-semibold text-purple-400 mb-2">Auto-update timestamps</h4>
                                            <pre className="bg-neutral-800 p-3 rounded text-xs text-gray-300 overflow-x-auto">
{`-- Function to update timestamps
CREATE OR REPLACE FUNCTION handle_updated_at()
RETURNS TRIGGER
LANGUAGE plpgsql
AS $$
BEGIN
  NEW.updated_at = now();
  RETURN NEW;
END;
$$;

-- Apply to all tables with updated_at
CREATE TRIGGER profiles_updated_at
  BEFORE UPDATE ON profiles
  FOR EACH ROW
  EXECUTE FUNCTION handle_updated_at();`}
                                            </pre>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Performance Optimization */}
                        <div className="bg-neutral-900/50 backdrop-blur-sm rounded-lg p-6 mb-8 border border-neutral-800">
                            <h2 className="text-2xl font-bold text-white mb-4">
                                ⚡ Performance Optimization
                            </h2>
                            <div className="grid md:grid-cols-2 gap-6">
                                <div>
                                    <h3 className="text-lg font-semibold text-green-400 mb-3">Indexing Strategy</h3>
                                    <ul className="text-gray-300 text-sm space-y-2">
                                        <li>• <strong>B-tree indexes:</strong> Primary keys, foreign keys, username lookups</li>
                                        <li>• <strong>GIN indexes:</strong> JSONB content and metadata searching</li>
                                        <li>• <strong>Partial indexes:</strong> Public pages, active subscriptions</li>
                                        <li>• <strong>Composite indexes:</strong> (page_id, order_index) for block ordering</li>
                                    </ul>
                                </div>
                                <div>
                                    <h3 className="text-lg font-semibold text-blue-400 mb-3">Query Optimization</h3>
                                    <ul className="text-gray-300 text-sm space-y-2">
                                        <li>• <strong>Selective fetching:</strong> Only required fields in API responses</li>
                                        <li>• <strong>Connection pooling:</strong> Supabase handles connection management</li>
                                        <li>• <strong>Prepared statements:</strong> Automatic query plan caching</li>
                                        <li>• <strong>JSONB operators:</strong> Efficient content querying and filtering</li>
                                    </ul>
                                </div>
                            </div>
                        </div>

                        {/* Next Steps */}
                        <div className="bg-indigo-500/10 border border-indigo-500/20 rounded-lg p-6">
                            <h3 className="text-lg font-semibold text-white mb-4">
                                🚀 Explore More
                            </h3>
                            <div className="grid md:grid-cols-2 gap-4">
                                <Link href="/support/documentation/auth" className="block p-4 rounded-lg bg-neutral-800/50 hover:bg-neutral-800 transition-colors">
                                    <div className="font-semibold text-red-400 mb-1">Authentication & Roles →</div>
                                    <div className="text-gray-300 text-sm">Learn how the permission system works in practice</div>
                                </Link>
                                <Link href="/support/documentation/api" className="block p-4 rounded-lg bg-neutral-800/50 hover:bg-neutral-800 transition-colors">
                                    <div className="font-semibold text-green-400 mb-1">API Documentation →</div>
                                    <div className="text-gray-300 text-sm">See how to query this data via REST API</div>
                                </Link>
                            </div>
                        </div>
                    </div>
                </motion.div>
            </div>
        </div>
    )
}